import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { BiddleDraft, BiddleDraftStep } from '@/types/models'

const DRAFT_STEPS: BiddleDraftStep[] = ['price', 'terms', 'timing', 'extras', 'review']

/** In-memory handoff so Drafts tab → create modal can skip a second read. */
let stashedDraft: BiddleDraft | null = null

export function stashDraftForResume(draft: BiddleDraft) {
  stashedDraft = draft
}

export function takeStashedDraft(propertyId: string): BiddleDraft | null {
  if (stashedDraft?.propertyId === propertyId) {
    const draft = stashedDraft
    stashedDraft = null
    return draft
  }
  return null
}

function asDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate()
  }
  return new Date()
}

export function parseBiddleDraft(id: string, data: Record<string, unknown>): BiddleDraft {
  const stepRaw = String(data.step ?? 'price')
  const step = (DRAFT_STEPS.includes(stepRaw as BiddleDraftStep)
    ? stepRaw
    : 'price') as BiddleDraftStep

  return {
    propertyId: String(data.propertyId ?? id),
    fullAddress: String(data.fullAddress ?? ''),
    step,
    agreed: Boolean(data.agreed),
    price: String(data.price ?? ''),
    settlement: String(data.settlement ?? '60'),
    deposit: String(data.deposit ?? '10'),
    flexibleSettlement: Boolean(data.flexibleSettlement),
    flexibleDeposit: Boolean(data.flexibleDeposit),
    isOpenOffer: Boolean(data.isOpenOffer),
    expiry: String(data.expiry ?? ''),
    notes: String(data.notes ?? ''),
    sendLetter: Boolean(data.sendLetter),
    updatedAt: asDate(data.updatedAt),
    createdAt: asDate(data.createdAt),
  }
}

export type SaveBiddleDraftInput = {
  propertyId: string
  fullAddress: string
  step: BiddleDraftStep
  agreed: boolean
  price: string
  settlement: string
  deposit: string
  flexibleSettlement: boolean
  flexibleDeposit: boolean
  isOpenOffer: boolean
  expiry: string
  notes: string
  sendLetter: boolean
}

export async function saveBiddleDraft(uid: string, input: SaveBiddleDraftInput): Promise<void> {
  if (!input.agreed) throw new Error('Agree to continue before saving a draft.')
  if (!DRAFT_STEPS.includes(input.step)) throw new Error('Cannot save a draft on this step.')

  const ref = doc(db, 'users', uid, 'biddleDrafts', input.propertyId)
  const existing = await getDoc(ref)
  const createdAt = existing.exists()
    ? (existing.data()?.createdAt ?? serverTimestamp())
    : serverTimestamp()

  await setDoc(ref, {
    propertyId: input.propertyId,
    fullAddress: input.fullAddress,
    step: input.step,
    agreed: true,
    price: input.price,
    settlement: input.settlement,
    deposit: input.deposit,
    flexibleSettlement: input.flexibleSettlement,
    flexibleDeposit: input.flexibleDeposit,
    isOpenOffer: input.isOpenOffer,
    expiry: input.expiry,
    notes: input.notes.slice(0, 239),
    sendLetter: input.sendLetter,
    updatedAt: serverTimestamp(),
    createdAt,
  })
}

export async function getBiddleDraft(uid: string, propertyId: string): Promise<BiddleDraft | null> {
  const snap = await getDoc(doc(db, 'users', uid, 'biddleDrafts', propertyId))
  if (!snap.exists()) return null
  return parseBiddleDraft(snap.id, snap.data() as Record<string, unknown>)
}

export async function listBiddleDrafts(uid: string): Promise<BiddleDraft[]> {
  const q = query(collection(db, 'users', uid, 'biddleDrafts'), orderBy('updatedAt', 'desc'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => parseBiddleDraft(d.id, d.data() as Record<string, unknown>))
}

export async function deleteBiddleDraft(uid: string, propertyId: string): Promise<void> {
  await deleteDoc(doc(db, 'users', uid, 'biddleDrafts', propertyId))
}
